
import Vue from "vue"
import axios, { AxiosError, AxiosResponse } from "axios"
import { EventBus } from "@/plugins/eventBus"
import AddressPositionMap from "@/components/AddressPositionMap.vue"

interface HealthService {
  code_sst: string
  code_urssaf: string | null
  name: string
  address: string
  complementary_address: string
  zip_code: string
  city: string
  phone: string
  isOrphaned?: boolean
}

interface UrssafOrganization {
  code_urssaf: string | null
  name: string
  isOrphaned?: boolean
}

export default Vue.extend({
  name: 'SpaceFormUpdate',
  components: {
    AddressPositionMap
  },
  data: () => ({
    id: null as string|null,
    formSpace: {
      id: '',
      name: '',
      siret: '',
      logo: '',
      url: '',
      address: '',
      zipcode: '',
      city: '',
      phone: '',
      default_notification_email: '',
      receive_notification_on_default_email: false,
      ocr: false,
      sms: false,
      mail: false,
      association_code: '',
      urssaf_city: '',
      code_urssaf: null,
      urssaf_health_services: [] as Array<HealthService>,
      address_position: null as any,
    },
    urssafOrganizations: [] as Array<UrssafOrganization>,
    urssafHealthServices: [] as Array<HealthService>,
    disabled: true,
    dataLoaded: {
      space: false,
      organizations: false,
      healthServices: false
    }
  }),
  computed: {
    requiredRule() {
      return (v: string) => !!v || this.$vuetify.lang.t('$vuetify.required_field')
    },

    allowedUrssafHealthServices() {
      return this.urssafHealthServices.filter((service: HealthService) => service.code_urssaf === this.formSpace.code_urssaf)
    },
    allDataLoaded() {
      return this.dataLoaded.space && this.dataLoaded.organizations && this.dataLoaded.healthServices
    },
    logo() {
      let src = "dispojob_label.svg"
      if (this.formSpace.logo && typeof this.formSpace.logo === "string" && this.formSpace.logo.trim() !== "") {
        src = this.formSpace.logo
      }
      if (["development", "test"].includes(process.env.NODE_ENV)) {
        try {
          return require(`@/assets/logos/${src}`)
        } catch (e) {
          // If the logo file is not found, fallback to the default logo
          return require("@/assets/logos/dispojob_label.svg")
        }
      } else {
        // In production, just return the path; no need to catch
        return `/img/logos/${src}`
      }
    }
  },
  mounted() {
    this.resetLoadingState()
    this.getSpace(Number(this.$route.params.id))
    this.fetchUrssafOrganizations()
    this.fetchUrssafHealthServices()
  },
  watch: {
    $route () {
      this.resetLoadingState()
      this.getSpace(Number(this.$route.params.id))
    },
    allDataLoaded(newVal) {
      if (newVal) {
        this.mergeOrphanedEntries()
      }
    }
  },
  methods: {
    updateSpace() {
      if ((this.$refs.form as Vue & { validate: () => boolean }).validate()) {
        const body: {[k: string]: any} = {}
        body.name = this.formSpace.name
        body.siret = this.formSpace.siret
        body.url = this.formSpace.url
        body.address = this.formSpace.address
        body.zipcode = this.formSpace.zipcode
        body.city = this.formSpace.city
        body.phone = this.formSpace.phone
        body.default_notification_email = this.formSpace.default_notification_email
        body.association_code = this.formSpace.association_code
        body.urssaf_city = this.formSpace.urssaf_city
        body.ocr = this.formSpace.ocr
        body.sms = this.formSpace.sms
        body.mail = this.formSpace.mail
        body.receive_notification_on_default_email = this.formSpace.receive_notification_on_default_email
        body.urssaf_organization = this.formSpace.code_urssaf
        body.urssaf_health_services = Array.isArray(this.formSpace.urssaf_health_services)
          ? this.formSpace.urssaf_health_services.map((service: any) => service.code_sst)
          : []

        axios
          .put(`/v1/admin/spaces/${this.$route.params.id}`, body)
          .then(() => {
            EventBus.$emit('listParagonDataReload')
            this.resetLoadingState()
            this.getSpace(Number(this.$route.params.id))
            EventBus.$emit('snackbar',{message: this.$vuetify.lang.t('$vuetify.space_successfully_updated')})
            this.disabled = true
          })
          .catch((error: AxiosError) => {
            EventBus.$emit('snackbar',{axiosError: error})
          })
      }
    },
    getSpace(id: number) {
      axios
        .get(`/v1/admin/spaces/${id}`)
        .then((response: AxiosResponse) => {
          Object.assign(this.formSpace, response.data)
          this.dataLoaded.space = true
        })
        .catch((error: AxiosError) => {
          EventBus.$emit('snackbar',{axiosError: error})
          this.dataLoaded.space = true
        })
        .finally(() =>{
          this.disabled = true
        })
    },
    fetchUrssafOrganizations() {
      axios.get('/v1/autocomplete?data=urssaf_organization').then((response: AxiosResponse) => {
        this.urssafOrganizations = response.data
        this.dataLoaded.organizations = true
      }).catch(() => {
        this.dataLoaded.organizations = true
      })
    },
    fetchUrssafHealthServices() {
      axios.get('/v1/autocomplete?data=urssaf_health_service').then((response: AxiosResponse) => {
        this.urssafHealthServices = response.data
        this.dataLoaded.healthServices = true
      }).catch(() => {
        this.dataLoaded.healthServices = true
      })
    },
    /**
     * Select or unselect a health service
     * @param item HealthService
     */
    selectHealthService(item: HealthService) {
      if (!Array.isArray(this.formSpace.urssaf_health_services)) {
        this.formSpace.urssaf_health_services = [];
      }
      let found = false
      for (const service of this.formSpace.urssaf_health_services) {
        if (service.code_sst === item.code_sst) {
          found = true
          break
        }
      }
      if (found) {
        let newList = []
        for (const service of this.formSpace.urssaf_health_services) {
          if (service.code_sst !== item.code_sst) {
            newList.push(service)
          }
        }
        this.formSpace.urssaf_health_services = newList
      } else {
        this.formSpace.urssaf_health_services.push(item)
      }
    },
    /**
     * Merge orphaned entries from API response into autocomplete lists
     */
    mergeOrphanedEntries() {
      // Handle orphaned URSSAF organization
      if (this.formSpace.code_urssaf && !this.urssafOrganizations.find(org => org.code_urssaf === this.formSpace.code_urssaf)) {
        const orphanedOrg: UrssafOrganization = {
          code_urssaf: this.formSpace.code_urssaf,
          name: this.$vuetify.lang.t('$vuetify.data_possibly_outdated') || 'Data possibly outdated',
          isOrphaned: true
        }
        this.urssafOrganizations.push(orphanedOrg)
      }

      // Handle orphaned health services
      if (Array.isArray(this.formSpace.urssaf_health_services)) {
        this.formSpace.urssaf_health_services.forEach(service => {
          if (service.code_sst && !this.urssafHealthServices.find(hs => hs.code_sst === service.code_sst)) {
            const orphanedService: HealthService = {
              code_sst: service.code_sst,
              code_urssaf: service.code_urssaf || this.formSpace.code_urssaf || null,
              name: service.name || this.$vuetify.lang.t('$vuetify.data_possibly_outdated') || 'Data possibly outdated',
              address: service.address || '',
              complementary_address: service.complementary_address || '',
              zip_code: service.zip_code || '',
              city: service.city || '',
              phone: service.phone || '',
              isOrphaned: true
            }
            this.urssafHealthServices.push(orphanedService)
          }
        })
      }
    },
    getUrssafOrganizationText(item: UrssafOrganization) {
      return `${item.code_urssaf || ''} - ${item.name}`
    },
    resetLoadingState() {
      this.dataLoaded = {
        space: false,
        organizations: false,
        healthServices: false
      }
    }
  }
})
